import type { CSSProperties, ReactNode } from "react";

export type Tone = "alarm" | "ok" | "warn" | "nodata" | "info" | "accent";

const TONE_DOT: Record<Tone, string> = {
  alarm: "var(--tone-alarm-dot)",
  ok: "var(--tone-ok-dot)",
  warn: "var(--tone-warn-dot)",
  nodata: "var(--tone-nodata-dot)",
  info: "var(--tone-info-dot)",
  accent: "var(--accent-dot)",
};

const TONE_BG: Record<Tone, string> = {
  alarm: "var(--tone-alarm-bg)",
  ok: "var(--tone-ok-bg)",
  warn: "var(--tone-warn-bg)",
  nodata: "var(--tone-nodata-bg)",
  info: "var(--tone-info-bg)",
  accent: "var(--accent-bg)",
};

const TONE_FG: Record<Tone, string> = {
  alarm: "var(--tone-alarm-fg)",
  ok: "var(--tone-ok-fg)",
  warn: "var(--tone-warn-fg)",
  nodata: "var(--tone-nodata-fg)",
  info: "var(--tone-info-fg)",
  accent: "var(--accent)",
};

interface CardProps {
  children: ReactNode;
  tight?: boolean;
  flush?: boolean;
  onClick?: () => void;
  className?: string;
  style?: CSSProperties;
}

export function Card({ children, tight, flush, onClick, className, style }: CardProps) {
  const classes = [
    "card",
    tight ? "card--tight" : "",
    flush ? "card--flush" : "",
    onClick ? "card--interactive" : "",
    className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  if (onClick) {
    return (
      <button type="button" className={classes} style={style} onClick={onClick}>
        {children}
      </button>
    );
  }
  return (
    <section className={classes} style={style}>
      {children}
    </section>
  );
}

interface PillProps {
  tone: Tone;
  children: ReactNode;
}

export function Pill({ tone, children }: PillProps) {
  return <span className={`pill pill--${tone}`}>{children}</span>;
}

interface KpiCardProps {
  label: string;
  icon?: ReactNode;
  value: ReactNode;
  badge?: ReactNode;
  badgeTone?: Tone;
  footnote?: ReactNode;
  valueColor?: string;
  onClick?: () => void;
}

export function KpiCard({ label, icon, value, badge, badgeTone, footnote, valueColor, onClick }: KpiCardProps) {
  return (
    <Card
      tight
      onClick={onClick}
      style={{ display: "flex", flexDirection: "column", gap: 7, padding: "16px 18px" }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span className="label">{label}</span>
        {icon && <span style={{ color: "var(--fg-faint)", display: "inline-flex", flex: "none" }}>{icon}</span>}
      </span>
      <span style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
        <span className="big-number" style={valueColor ? { color: valueColor } : undefined}>
          {value}
        </span>
        {badge && <Pill tone={badgeTone ?? "nodata"}>{badge}</Pill>}
      </span>
      {footnote && (
        <span style={{ fontSize: 12, lineHeight: 1.45, color: "var(--fg-subtle)" }}>{footnote}</span>
      )}
    </Card>
  );
}

interface ChipProps {
  active: boolean;
  onClick: () => void;
  dot?: string;
  count?: ReactNode;
  children: ReactNode;
}

export function Chip({ active, onClick, dot, count, children }: ChipProps) {
  return (
    <button type="button" className={active ? "chip chip--active" : "chip"} onClick={onClick}>
      {dot && <span className="chip__dot" style={{ background: dot }} />}
      {children}
      {count !== undefined && <span className="chip__count mono">{count}</span>}
    </button>
  );
}

interface StatusDotProps {
  tone?: Tone;
  color?: string;
  square?: boolean;
  size?: number;
}

export function StatusDot({ tone, color, square, size = 8 }: StatusDotProps) {
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: "none",
        display: "inline-block",
        borderRadius: square ? 3 : 999,
        background: color ?? TONE_DOT[tone ?? "nodata"],
      }}
    />
  );
}

interface ProgressBarProps {
  pct: number;
  tone?: Tone;
  color?: string;
}

export function ProgressBar({ pct, tone, color }: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <span
      style={{
        display: "block",
        height: 6,
        borderRadius: 999,
        background: "var(--bg-subtle)",
        border: "1px solid var(--divider)",
        overflow: "hidden",
      }}
    >
      <span
        style={{
          display: "block",
          height: "100%",
          width: `${clamped}%`,
          borderRadius: 999,
          background: color ?? TONE_DOT[tone ?? "accent"],
          transition: "width 0.3s ease",
        }}
      />
    </span>
  );
}

interface EmptyStateProps {
  title: string;
  message?: string;
  icon?: ReactNode;
  tone?: Tone;
}

export function EmptyState({ title, message, icon, tone = "ok" }: EmptyStateProps) {
  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "24px 12px",
        textAlign: "center",
      }}
    >
      {icon && (
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 999,
            background: TONE_BG[tone],
            color: TONE_FG[tone],
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </span>
      )}
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--fg)" }}>{title}</span>
      {message && <span style={{ fontSize: 12, color: "var(--fg-subtle)", maxWidth: 260 }}>{message}</span>}
    </div>
  );
}

interface SectionTitleProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
}

export function SectionTitle({ title, subtitle, actions }: SectionTitleProps) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: 12,
        flexWrap: "wrap",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span className="display" style={{ fontSize: 14.5 }}>
          {title}
        </span>
        {subtitle && <span style={{ fontSize: 11.5, color: "var(--fg-subtle)" }}>{subtitle}</span>}
      </div>
      {actions && (
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>{actions}</div>
      )}
    </div>
  );
}
